import { useState, useRef } from "react";
import { galleryImages } from "@/mocks/images";

const categories = ["Todos"];

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const filtered = activeCategory === "Todos"
    ? galleryImages
    : galleryImages.filter((g) => g.category === activeCategory);

  const handleVideoClick = (src: string) => {
    const video = videoRefs.current[src];
    if (!video) return;
    if (playingVideo === src) {
      video.pause();
      setPlayingVideo(null);
    } else {
      if (playingVideo && videoRefs.current[playingVideo]) {
        videoRefs.current[playingVideo]!.pause();
      }
      video.play();
      setPlayingVideo(src);
    }
  };

  return (
    <section id="galeria" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-cyan-500 text-sm font-semibold uppercase tracking-widest">Galería</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-800 mt-3 leading-tight">
              Trabajos{" "}
              <span className="italic text-cyan-500">Realizados</span>
            </h2>
          </div>
          <p className="text-slate-500 max-w-sm text-sm md:text-right">
            Cada proyecto es único. Descubre algunos de nuestros trabajos más destacados.
          </p>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-cyan-500 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {filtered.map((item) => (
            <div
              key={item.img}
              className="break-inside-avoid rounded-2xl overflow-hidden relative group cursor-pointer"
            >
              {item.type === "video" ? (
                <div className="relative w-full" onClick={() => handleVideoClick(item.img)}>
                  <video
                    ref={(el) => { videoRefs.current[item.img] = el; }}
                    src={item.img}
                    className="w-full object-cover object-top"
                    playsInline
                    loop
                    onPlay={() => setPlayingVideo(item.img)}
                    onPause={() => setPlayingVideo((prev) => prev === item.img ? null : prev)}
                  />
                  {/* Play/Pause overlay */}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playingVideo === item.img ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
                    <div className="w-14 h-14 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full border-2 border-white/60">
                      <i className={`${playingVideo === item.img ? "ri-pause-fill" : "ri-play-fill"} text-white text-2xl`}></i>
                    </div>
                  </div>
                  {/* Video badge */}
                  <div className="absolute top-3 right-3 bg-cyan-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-video-line text-xs"></i>
                    Video
                  </div>
                  {/* Bottom info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                    <div>
                      <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wider">{item.category}</span>
                      <p className="text-white font-bold text-base mt-1">{item.title}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                    <div>
                      <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wider">{item.category}</span>
                      <p className="text-white font-bold text-base mt-1">{item.title}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm mb-4">¿Quieres ver más proyectos o empezar el tuyo?</p>
          <button
            onClick={() => {
              const el = document.getElementById("contacto");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="bg-slate-800 hover:bg-slate-700 text-white font-semibold px-8 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            Solicita tu Proyecto →
          </button>
        </div>
      </div>
    </section>
  );
}
